import React from "react"
import PropTypes from "prop-types"
import InfoDataPanel from "src/v2/components/InfoDataPanel/info-data-panel"

const formatCPFCNPJ = (value) => {
    if (!value) return "-"

    const digits = value.replace(/\D/g, "")

    if (digits.length === 8) {
        return digits.replace(/(\d{2})(\d{3})(\d{3})/, "$1.$2.$3")
    }

    if (digits.length === 11) {
        return digits.replace(
            /(\d{3})(\d{3})(\d{3})(\d{2})/,
            "$1.$2.$3-$4"
        )
    }

    if (digits.length === 14) {
        return digits.replace(
            /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
            "$1.$2.$3/$4-$5"
        )
    }

    return value
}

const cnpjOptionalTextProps = {
    optionalTextOpacity: 0.6,
    optionalTextVariant: "captionInfo",
}

const sections = [
    {
        variant: "divided",
        items: [
            {
                key: "Emissor",
                label: "Emissor",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_Emissor),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Devedor",
                label: "Devedor",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_Devedor),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "GrupoEconomico",
                label: "Grupo Econômico",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CNPJGrupoEconomico),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Setor",
                label: "Setor",
                formatter: (_, data) => data?.Setor ?? "-",
                optionalTextLabel: "Subsetor",
                optionalTextFormatter: (data) => data?.SubSetor ?? "-",
            },
        ],
    },
    {
        items: [
            {
                key: "ResumoDevedor",
                label: "Resumo Devedor",
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
    {
        items: [
            {
                key: "Coobrigacao",
                label: "Coobrigação",
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
    {
        variant: "divided",
        items: [
            {
                key: "AgenteFiduciario",
                label: "Agente Fiduciário",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_AgenteFiduciario),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "AgenciaRating",
                label: "Agência de Rating",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_AgenciaRating),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Auditoria",
                label: "Auditoria",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_Auditoria),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "ContaRecebedora",
                label: "Conta Recebedora",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_ContaRecebedora),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
    {
        variant: "divided",
        items: [
            {
                key: "Custodiante",
                label: "Custodiante",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_Custodiante),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Cedente",
                label: "Cedente",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_Cedente),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Depositario",
                label: "Depositário",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_Depositario),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Escriturador",
                label: "Escriturador",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_Escriturador),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
    {
        variant: "divided",
        sx: {
            width: { xxxs: "100%", md: "25%" },
        },
        items: [
            {
                key: "Registrador",
                label: "Registrador",
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPF_CNPJ_Registrador),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
]

export const ContrapartesPanel = ({ characterData }) => {
    return (
        <InfoDataPanel
            title="Contrapartes: CPF, CNPJ"
            data={characterData}
            sections={sections}
        />
    )
}

ContrapartesPanel.propTypes = {
    characterData: PropTypes.shape({
        Emissor: PropTypes.string,
        Devedor: PropTypes.string,
        GrupoEconomico: PropTypes.string,
        Setor: PropTypes.string,
        SubSetor: PropTypes.string,
        ResumoDevedor: PropTypes.string,
        Coobrigacao: PropTypes.string,
        AgenteFiduciario: PropTypes.string,
        AgenciaRating: PropTypes.string,
        Auditoria: PropTypes.string,
        ContaRecebedora: PropTypes.string,
        Custodiante: PropTypes.string,
        Cedente: PropTypes.string,
        Depositario: PropTypes.string,
        Escriturador: PropTypes.string,
        Registrador: PropTypes.string,

        CNPJGrupoEconomico: PropTypes.string,
        CPF_CNPJ_Emissor: PropTypes.string,
        CPF_CNPJ_Devedor: PropTypes.string,
        CPF_CNPJ_AgenteFiduciario: PropTypes.string,
        CPF_CNPJ_AgenciaRating: PropTypes.string,
        CPF_CNPJ_Auditoria: PropTypes.string,
        CPF_CNPJ_ContaRecebedora: PropTypes.string,
        CPF_CNPJ_Custodiante: PropTypes.string,
        CPF_CNPJ_Cedente: PropTypes.string,
        CPF_CNPJ_Depositario: PropTypes.string,
        CPF_CNPJ_Escriturador: PropTypes.string,
        CPF_CNPJ_Registrador: PropTypes.string,
    }),
}